import React from "react";

import { BoxProps, TypographyProps } from "@mui/material";
import Box from "@mui/material/Box";
import LinearProgress, {
  LinearProgressProps,
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { Theme } from "@mui/material/styles";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { Column, Row } from "@rui/Stack";

type LinearProgressWithLabelProps = LinearProgressProps & {
  value: number;
  status: "loading" | "complete" | "error";
  icon?: React.ReactNode;
};

const getProgressColor = (
  theme: Theme,
  status: "loading" | "complete" | "error"
) => {
  switch (status) {
    case "loading":
      return theme.palette.shades.primary[600];
    case "complete":
      return theme.palette.shades.meadow[600];
    case "error":
      return theme.palette.error.main;
  }
};

const Label = styled(
  ({
    status: _,
    ...props
  }: TypographyProps & { status: LinearProgressWithLabelProps["status"] }) => (
    <Typography variant="bodySecondaryReg" {...props} />
  )
)(({ theme, status }) => ({
  color: getProgressColor(theme, status),
}));

const IconWrapper = styled(
  ({
    status: _,
    ...props
  }: BoxProps & { status: LinearProgressWithLabelProps["status"] }) => (
    <Box {...props} />
  )
)(({ theme, status }) => ({
  color: getProgressColor(theme, status),
  height: "20px",
  "& svg": { width: "16px", height: "16px" },
}));

const BorderLinearProgress = styled(
  ({ status: _s, icon: _i, ...props }: LinearProgressWithLabelProps) => (
    <LinearProgress {...props} />
  )
)(({ theme, status }) => ({
  height: 6,
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.shades.neutral[100],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    backgroundColor: getProgressColor(theme, status),
  },
}));

const LinearProgressWithLabel = (props: LinearProgressWithLabelProps) => {
  return (
    <Row variant="apart" spacing={1}>
      <BorderLinearProgress
        variant="determinate"
        {...props}
        sx={{ width: "100%" }}
      />
      <Column variant={props.icon ? "left" : "right"} sx={{ minWidth: 35 }}>
        {props.icon ? (
          <IconWrapper status={props.status}>{props.icon}</IconWrapper>
        ) : (
          <Label status={props.status}>{Math.round(props.value)}%</Label>
        )}
      </Column>
    </Row>
  );
};

export default LinearProgressWithLabel;
